<template>
  <div ref="modal" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("dashboardBlocks.wizard.title") }}</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :aria-label="$t('confirmModal.close')"
          ></button>
        </div>

        <ol class="wizard-steps">
          <li
            v-for="n in 3"
            :key="n"
            :class="{ active: step === n, done: step > n }"
          >
            <span class="wizard-step-number" aria-hidden="true">{{
              step > n ? "✓" : n
            }}</span>
            {{ $t(`dashboardBlocks.wizard.step${n}`) }}
          </li>
        </ol>

        <div class="modal-body">
          <div v-if="step === 1">
            <label class="form-label" for="dashboard-block-prompt">{{
              $t("dashboardBlocks.wizard.promptLabel")
            }}</label>
            <textarea
              id="dashboard-block-prompt"
              v-model="prompt"
              class="form-control"
              rows="4"
              maxlength="1000"
              :disabled="loading"
              :placeholder="$t('dashboardBlocks.wizard.promptPlaceholder')"
            ></textarea>
            <div class="wizard-chips">
              <button
                v-for="example in examples"
                :key="example"
                type="button"
                class="btn btn-small btn-secondary"
                :disabled="loading"
                @click="prompt = example"
              >
                {{ example }}
              </button>
            </div>
            <p v-if="loading" class="text-gray mt-3 mb-0" role="status">
              {{ $t("dashboardBlocks.wizard.working") }}
            </p>
          </div>

          <p
            v-else-if="loading && !result"
            class="text-gray mb-0"
            role="status"
          >
            {{ $t("dashboardBlocks.wizard.loading") }}
          </p>

          <div v-else-if="step === 2 && result" class="row g-4">
            <div class="col-lg-4">
              <h6>{{ $t("dashboardBlocks.wizard.whatItDoes") }}</h6>
              <ul class="wizard-facts">
                <li v-for="fact in result.facts" :key="fact">{{ fact }}</li>
              </ul>
              <div
                v-if="result.check"
                class="wizard-check"
                :class="result.check.matches ? 'ok' : 'differs'"
                role="status"
              >
                <template v-if="result.check.matches">{{
                  $t("dashboardBlocks.wizard.checkOk", {
                    value: checkValue(result.check, "block"),
                  })
                }}</template>
                <template v-else>{{
                  $t("dashboardBlocks.wizard.checkDiffers", {
                    block: checkValue(result.check, "block"),
                    reference: checkValue(result.check, "reference"),
                  })
                }}</template>
              </div>
              <label class="form-label mt-3" for="dashboard-block-feedback">{{
                $t("dashboardBlocks.wizard.feedbackLabel")
              }}</label>
              <textarea
                id="dashboard-block-feedback"
                v-model="feedback"
                class="form-control"
                rows="3"
                maxlength="1000"
                :disabled="loading"
                :placeholder="$t('dashboardBlocks.wizard.feedbackPlaceholder')"
              ></textarea>
              <button
                type="button"
                class="btn btn-secondary btn-small spin-dark mt-2"
                :class="{ 'loading-spin': loading }"
                :disabled="loading || !feedback.trim()"
                @click="generate(true)"
              >
                <span>{{ $t("dashboardBlocks.wizard.change") }}</span>
              </button>
            </div>
            <div class="col-lg-8">
              <h6>{{ $t("dashboardBlocks.wizard.preview") }}</h6>
              <CustomBlock
                :model-value="{ block: { id: null, layout: result.layout } }"
                :preview="result.preview"
              />
            </div>
          </div>

          <div v-else-if="step === 3 && result">
            <label class="form-label" for="dashboard-block-name">{{
              $t("dashboardBlocks.wizard.name")
            }}</label>
            <input
              id="dashboard-block-name"
              v-model="name"
              class="form-control mb-4"
              maxlength="120"
            />
            <template v-if="!editing">
              <label class="form-label d-block">{{
                $t("dashboardBlocks.wizard.width")
              }}</label>
              <SegmentedControl
                v-model="size"
                :options="sizes"
                :aria-label="$t('dashboardBlocks.wizard.width')"
              />
            </template>
          </div>
        </div>

        <div class="modal-footer">
          <button
            v-if="editing && step === 2"
            type="button"
            class="btn btn-small btn-danger me-auto"
            :disabled="loading"
            @click="remove"
          >
            {{ $t("dashboardBlocks.wizard.delete") }}
          </button>
          <button
            v-if="step > firstStep"
            type="button"
            class="btn btn-secondary me-auto"
            :disabled="loading || saving"
            @click="step--"
          >
            {{ $t("dashboardBlocks.wizard.back") }}
          </button>
          <button
            v-if="step === 1"
            type="button"
            class="btn btn-primary"
            :class="{ 'loading-spin': loading }"
            :disabled="loading || prompt.trim().length < 5"
            @click="generate(false)"
          >
            <span>{{ $t("dashboardBlocks.wizard.generate") }}</span>
          </button>
          <button
            v-else-if="step === 2"
            type="button"
            class="btn btn-primary"
            :disabled="loading"
            @click="step = 3"
          >
            <span>{{ $t("dashboardBlocks.wizard.next") }}</span>
          </button>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            :class="{ 'loading-spin': saving }"
            :disabled="saving || !name.trim()"
            @click="save"
          >
            <span>{{
              editing
                ? $t("dashboardBlocks.wizard.save")
                : $t("dashboardBlocks.wizard.add")
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import http from "@/modules/http";
import CustomBlock from "../home/CustomBlock.vue";
import SegmentedControl from "../inputs/SegmentedControl.vue";
const bootstrap = require("bootstrap");

export default {
  name: "DashboardBlockWizard",
  components: { CustomBlock, SegmentedControl },
  emits: ["saved", "delete"],
  data: function () {
    return {
      store: useStore(),
      myModal: undefined,
      step: 1,
      prompt: "",
      feedback: "",
      name: "",
      size: 12,
      result: null,
      loading: false,
      saving: false,
      editing: null,
    };
  },
  computed: {
    firstStep() {
      return this.editing ? 2 : 1;
    },
    examples() {
      return [1, 2, 3].map((n) =>
        this.$t(`dashboardBlocks.wizard.example${n}`)
      );
    },
    sizes() {
      return [4, 6, 8, 12].map((value) => ({
        value,
        label: this.$t(`dashboardBlocks.wizard.size${value}`),
      }));
    },
  },
  methods: {
    open(block = null) {
      Object.assign(this, {
        editing: block,
        step: 1,
        prompt: "",
        feedback: "",
        name: "",
        size: 12,
        result: null,
        loading: false,
        saving: false,
      });
      if (this.myModal === undefined) {
        this.myModal = new bootstrap.Modal(this.$refs.modal, {
          backdrop: "static",
        });
      }
      this.myModal.show();
      if (block) this.load(block);
    },
    load(block) {
      const topBar = this.store.state.topBar;
      const data = {};
      if (topBar.date?.[0]) data.from = this.day(topBar.date[0]);
      if (topBar.date?.[1]) data.to = this.day(topBar.date[1]);
      if (topBar.warehouse) data.warehouse = topBar.warehouse;

      this.loading = true;
      http
        .fetch(`/dashboard/custom-blocks/${block.id}/draft`, data)
        .then((result) => {
          this.result = result;
          this.prompt = result.prompt;
          this.name = result.name;
          this.step = 2;
        })
        .catch(() => this.myModal.hide())
        .finally(() => {
          this.loading = false;
        });
    },
    remove() {
      this.myModal.hide();
      this.$emit("delete", this.editing);
    },
    day(value) {
      return value instanceof Date ? value.toISOString().split("T")[0] : value;
    },
    checkValue(check, key) {
      const value = new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 0,
      }).format(check[key]);
      return value;
    },
    generate(refine) {
      const topBar = this.store.state.topBar;
      const data = { prompt: this.prompt.trim() };
      if (topBar.date?.[0]) data.from = this.day(topBar.date[0]);
      if (topBar.date?.[1]) data.to = this.day(topBar.date[1]);
      if (topBar.warehouse) data.warehouse = topBar.warehouse;
      if (refine) {
        data.draft = this.result.draft;
        data.feedback = this.feedback.trim();
      }

      this.loading = true;
      http
        .fetch("/dashboard/custom-blocks/generate", data)
        .then((result) => {
          this.result = result;
          this.name = result.name;
          this.feedback = "";
          this.step = 2;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saving = true;
      const edited = this.editing;
      http
        .fetch(
          edited
            ? `/dashboard/custom-blocks/${edited.id}`
            : "/dashboard/custom-blocks",
          { draft: this.result.draft, name: this.name.trim() },
          false,
          edited ? "PUT" : null
        )
        .then((block) => {
          this.myModal.hide();
          this.$emit("saved", { block, size: this.size, edited: !!edited });
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.wizard-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  margin: 0;
  padding: 1rem 1.5rem;
  list-style: none;
  border-bottom: 1px solid var(--pf-border);
}
.wizard-steps li {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.9375rem;
  color: var(--pf-muted);
}
.wizard-steps li.active {
  font-weight: 800;
  color: var(--pf-text);
}
.wizard-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: var(--pf-border);
  font-size: 0.8125rem;
  font-weight: 800;
  color: var(--pf-text);
}
.wizard-steps li.active .wizard-step-number {
  background: var(--pf-accent);
  color: #fff;
}
.wizard-steps li.done .wizard-step-number {
  background: #1c8c5e;
  color: #fff;
}
.wizard-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.wizard-facts {
  padding-left: 1.25rem;
  font-size: 0.875rem;
  line-height: 1.5;
}
.wizard-check {
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  line-height: 1.45;
}
.wizard-check.ok {
  background: #f0faf5;
  border: 1px solid #bfe3d2;
}
.wizard-check.differs {
  background: #fff7ec;
  border: 1px solid #f0d3a8;
}
</style>
